import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Modal,
  Tag,
  Tooltip,
  Popconfirm,
  Select,
  message
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  UsergroupAddOutlined,
  ApartmentOutlined,
  CheckOutlined,
  CloseOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  fetchRbacRoles,
  createRbacRole,
  updateRbacRole,
  deleteRbacRole,
  setRbacEditing,
  clearRbacEditing,
  fetchRoleUsers
} from '../../redux/actions/rbacActions';
import RoleForm from './RoleForm';

const { Option } = Select;

const RolesTab = () => {
  const dispatch = useDispatch();
  const { roles, rolesLoading, roleUsers } = useSelector(state => state.rbac);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [viewUsersModal, setViewUsersModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [statusFilter, setStatusFilter] = useState(undefined);

  useEffect(() => {
    dispatch(fetchRbacRoles({ enable: statusFilter }));
  }, [dispatch, statusFilter]);

  const handleAddNew = () => {
    dispatch(clearRbacEditing());
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    dispatch(setRbacEditing(record));
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteRbacRole(id));
  };

  const handleViewUsers = (role) => {
    setSelectedRole(role);
    dispatch(fetchRoleUsers(role.id));
    setViewUsersModal(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    dispatch(clearRbacEditing());
  };

  const handleRefresh = () => {
    dispatch(fetchRbacRoles({ enable: statusFilter }));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Role Name',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: 'Enable',
      dataIndex: 'enable',
      key: 'enable',
      width: 100,
      render: (enable) => (
        <Tag color={enable ? 'success' : 'error'}>
          {enable ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status ? 'processing' : 'default'}>
          {status ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'created_on',
      key: 'created_on',
      width: 180,
      render: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Users">
            <Button
              type="text"
              icon={<UsergroupAddOutlined />}
              onClick={() => handleViewUsers(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Popconfirm
            title="Delete Role"
            description="Are you sure you want to delete this role?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '1rem' }}>
      {/* Stats Cards */}
      <div className="rbac-stats-grid">
        <div className="rbac-stat-card">
          <div className="rbac-stat-icon"><ApartmentOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{roles.length}</div>
            <div className="rbac-stat-label">Total Roles</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#28a745' }}>
          <div className="rbac-stat-icon" style={{ background: '#d4edda', color: '#155724' }}><CheckOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{roles.filter(r => r.enable && r.status).length}</div>
            <div className="rbac-stat-label">Active Roles</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#dc3545' }}>
          <div className="rbac-stat-icon" style={{ background: '#f8d7da', color: '#721c24' }}><CloseOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{roles.filter(r => !r.enable || !r.status).length}</div>
            <div className="rbac-stat-label">Inactive Roles</div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="modern-table-container" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <h2 className="table-title">Roles Management</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Select
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: 180 }}
            >
              <Option value={1}>Enabled</Option>
              <Option value={0}>Disabled</Option>
            </Select>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <ReloadOutlined /> Refresh
            </button>
            <button className="btn btn-primary" onClick={handleAddNew}>
              <PlusOutlined /> Add New Role
            </button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={rolesLoading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} roles`,
          }}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <div className="empty-state">
                <div className="empty-state-icon"><ApartmentOutlined /></div>
                <div className="empty-state-text">No roles found</div>
                <button className="btn btn-primary" onClick={handleAddNew}>
                  Add Your First Role
                </button>
              </div>
            ),
          }}
        />
      </div>

      {/* Role Form Modal */}
      <Modal
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnClose
        centered
        title={selectedRole ? 'Edit Role' : 'Create New Role'}
      >
        <RoleForm onCancel={handleModalClose} />
      </Modal>

      {/* View Users Modal */}
      <Modal
        open={viewUsersModal}
        onCancel={() => setViewUsersModal(false)}
        footer={null}
        width={700}
        destroyOnClose
        centered
        title={`Users with Role: ${selectedRole?.role_name}`}
      >
        <div className="role-users-list">
          {roleUsers.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem' }}>
              <div className="empty-state-icon"><TeamOutlined /></div>
              <div className="empty-state-text">No users assigned to this role</div>
            </div>
          ) : (
            roleUsers.map(user => (
              <div key={user.id} className="user-role-item">
                <div className="user-role-info">
                  <div className="user-role-avatar">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="user-role-details">
                    <h4>{user.name}</h4>
                    <p>{user.email}</p>
                  </div>
                </div>
                <Tag color={user.is_verified ? 'success' : 'warning'}>
                  {user.is_verified ? 'Verified' : 'Not Verified'}
                </Tag>
              </div>
            ))
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RolesTab;
